import type { LegalDocument } from '@/schemas/schema';
import { signatureRollout } from '@/modules/templates/services/signature.service';
import type { SignatureField } from '@/modules/templates/components/review/SignatureFieldOverlay';

export interface SendSignatory {
  name: string;
  email: string;
  role: string;
  order: number | null;
}

interface SendParams {
  /** The PDF the signature service will sign, as raw bytes. */
  pdfBytes: Uint8Array;
  documentId: string;
  templateSlug: string;
  templateTitle: string;
  numPages: number;
  signatureFields: SignatureField[];
  signatories: SendSignatory[];
}

export interface SendResult {
  documentId: string;
  trackingId: string;
  fileId: string;
}

/**
 * Sends the fixed-DOCX contract for signature, reusing the exact SELISE rollout pipeline
 * used by the AI-PDF review. Coordinates are scaled 72pt → 96px (page-size independent),
 * the already-converted PDF is fetched and base64-encoded, and a minimal LegalDocument-shaped
 * object satisfies the shared service (it only reads title + first signatory name for the
 * filename). On success the caller navigates to the shared success page.
 */
export async function sendDocxForSignature({
  pdfBytes,
  documentId,
  templateSlug,
  templateTitle,
  numPages,
  signatureFields,
  signatories,
}: SendParams): Promise<SendResult> {
  const DPI_SCALE = 96 / 72;
  const signatureFieldsForAPI = signatureFields.map((field) => ({
    id: field.id,
    type: field.type,
    signatoryIndex: field.signatoryIndex,
    pageNumber: field.pageNumber || 1,
    x: Math.round(field.x * DPI_SCALE),
    y: Math.round(field.y * DPI_SCALE),
    width: Math.round(field.width * DPI_SCALE),
    height: Math.round(field.height * DPI_SCALE),
    label: field.label || (field.type === 'signature' ? 'Signature' : 'Date'),
  }));

  const signatoriesForAPI = [...signatories]
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map((sig, idx) => ({
      party: sig.role || `signatory_${idx}`,
      name: sig.name,
      email: sig.email,
      role: sig.role,
      order: sig.order ?? idx + 1,
    }));

  // Minimal LegalDocument — the shared service only reads metadata.title + signatories[0].name.
  const minimalDocument = {
    metadata: { title: templateTitle, documentType: templateSlug },
    signatories: signatoriesForAPI.map((s) => ({
      name: s.name,
      email: s.email,
      party: s.party,
    })),
  } as unknown as LegalDocument;

  const binary = Array.from(pdfBytes, (byte) => String.fromCharCode(byte)).join('');
  const pdfBase64 = btoa(binary);

  const rolloutResult = await signatureRollout({
    pdfBase64,
    document: minimalDocument,
    formData: {},
    signatories: signatoriesForAPI,
    signatureFields: signatureFieldsForAPI,
    templateSlug,
    templateTitle,
    numPages: numPages || 1,
    draftId: documentId,
  });

  // Hand off to the shared success page (reads these from sessionStorage).
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('signature-last-result', JSON.stringify(rolloutResult));
    window.sessionStorage.setItem(
      'signature-last-payload',
      JSON.stringify({
        document: minimalDocument,
        formData: {},
        signatories: signatoriesForAPI,
        templateSlug,
        templateTitle,
      })
    );
  }

  return {
    documentId: rolloutResult.documentId,
    trackingId: rolloutResult.trackingId,
    fileId: rolloutResult.fileId,
  };
}
